import { useCallback, useContext, useMemo, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'
import moment from 'moment'

import { Button } from '~/components/Buttons'
import { FeedbackContext } from '~/components/feedback'
import Form from '~/components/unform/Form'
import NameAndTimeInterval from '~/components/unform/FormGroup/NameAndTimeInterval'
import {
  getWhiteLabelPublisherId,
  handleValidationsErrorsAPI,
  history
} from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useErrorApiAlert } from '~/hooks/useErrorApiAlert'
import useQueryParams from '~/hooks/useQuery'
import AdvancedSettings from '~/modules/retailMedia/components/CampaignForm/elements/Advanced'
import Budget from '~/modules/retailMedia/components/CampaignForm/elements/Budget'
import CampaignDestinationUrl from '~/modules/retailMedia/components/CampaignForm/elements/CampaignDestinationUrl'
import CampaignTargeting from '~/modules/retailMedia/components/CampaignForm/elements/CampaignTargeting'
import SponsoredBrand from '~/modules/retailMedia/components/CampaignForm/elements/SponsoredBrand'
import { useCampaignForm } from '~/modules/retailMedia/components/CampaignForm/hooks/useCampaignForm'
import { usePrepareCampaignCreateBody } from '~/modules/retailMedia/components/CampaignForm/hooks/usePrepareCampaignCreateBody'
import { useSubmitErrorMessage } from '~/modules/retailMedia/components/CampaignForm/hooks/useSubmitErrorMessage'
import * as S from '~/modules/retailMedia/components/CampaignForm/styles'
import ProductsPanel from '~/modules/retailMedia/components/ProductsPanel'
import {
  createCampaignKey,
  createCampaignService
} from '~/modules/retailMedia/services/campaign'
import { useAppSelector } from '~/store/hooks'

import CampaignType from '../../../../elements/CampaignType'
import { bypassLogoValidation } from '../../../../elements/SponsoredBrand/FieldsetLogo/bypassLogoValidation'
import Subpublishers from '../../../../elements/Subpublishers'
import { applyPreparedCampaignBody } from '../../../../helpers/applyPreparedCampaignBody'
import { withDefaultAgeRestrictedSetting } from '../../../helpers'
import { DTOCreateSponsoredBrandsCampaign } from './dto'

const SponsoredBrands: React.FC = () => {
  const { toast } = useContext(FeedbackContext)

  const [targetingContentIsValid, setTargetingContentIsValid] = useState(false)
  const [campaignTargeting, setCampaignTargeting] = useState<
    | undefined
    | {
        targeting: RetailMediaCampaignSegmentation
        data: { keywords: string[] | null; categories: Tag[] | null }
      }
  >()

  const [budgetValueIsValid, setBudgetValueIsValid] = useState(false)

  const [assetsIsValid, setAssetsIsValid] = useState({
    logo: false,
    assets: false
  })

  const handleAssetsIsValid = useCallback(
    ({ key, value }: { key: string; value: boolean }) => {
      setAssetsIsValid(prev => ({ ...prev, [key]: value }))
    },
    []
  )

  const {
    formRef,
    defaultCampaignName,
    audienceId,
    setCampaignPeriod,
    handleDebugSubmit,
    toggleDebugMode
  } = useCampaignForm()
  const { prepareCampaignCreateBody, isLinkedInsertionOrderReady } =
    usePrepareCampaignCreateBody()

  /**
   * Handle products
   */
  const {
    campaign: {
      products: { selectedProducts }
    }
  } = useAppSelector(state => state.retailMedia)

  const rmid = useQueryParams().get('rmid') || getWhiteLabelPublisherId()

  const formDataFinal = useMemo(() => {
    const getTargeting = () => {
      if (campaignTargeting?.targeting === 'category') {
        return {
          categories: String(
            campaignTargeting?.data?.categories.map(item => item.id)
          )
        }
      }

      if (campaignTargeting?.targeting === 'keyword') {
        return {
          keywords: campaignTargeting?.data?.keywords
        }
      }

      return {}
    }

    const products = selectedProducts?.map(item => ({
      id: item?.id,
      sku: item?.product_sku || item?.sku
    }))

    return {
      name: 'name',
      start_date: moment().format('YYYY-MM-DD'),
      type: 'on_site',
      targeting: getTargeting(),
      products
    }
  }, [
    campaignTargeting?.data?.categories,
    campaignTargeting?.data?.keywords,
    campaignTargeting?.targeting,
    selectedProducts
  ])

  /**
   * Check data validity
   */
  const formIsValid = useMemo(() => {
    const messages = [].filter(item => !!item)

    const hasProducts = !!selectedProducts?.length
    const hasSegmentation =
      campaignTargeting?.data?.categories?.length ||
      campaignTargeting?.data?.keywords?.length

    return {
      isValid: hasProducts && hasSegmentation,
      messages
    }
  }, [
    campaignTargeting?.data?.categories?.length,
    campaignTargeting?.data?.keywords?.length,
    selectedProducts?.length
  ])

  /**
   * Form is ready
   * Valor para controlarmos se devemos bloquear o submit
   */
  const allowBypassLogoValidation = useMemo(
    () => bypassLogoValidation(rmid as PublisherId),
    [rmid]
  )

  const formIsReadyToSubmit = useMemo(
    () =>
      budgetValueIsValid &&
      (allowBypassLogoValidation || assetsIsValid.logo) &&
      assetsIsValid.assets &&
      isLinkedInsertionOrderReady,
    [
      assetsIsValid?.assets,
      assetsIsValid?.logo,
      budgetValueIsValid,
      allowBypassLogoValidation,
      isLinkedInsertionOrderReady
    ]
  )

  /**
   * Handle request
   */

  const { submitErrorMessage, resolveSubmitError } = useSubmitErrorMessage()

  const [AlertErrorCampaign, setErrorCampaign] = useErrorApiAlert({
    title: submitErrorMessage
  })

  const { mutate, isPending } = useMutation({
    mutationKey: [createCampaignKey],
    mutationFn: createCampaignService,
    onSuccess: response => {
      history.push(`/retail-media/campaign/view/${response.data.id}`)

      toast.success(t('rm:campaign.create.toast.success.message'))
    },
    onError: error => {
      setErrorCampaign(error)

      handleValidationsErrorsAPI(error, formRef)

      toast.critical(resolveSubmitError(error))
    }
  })

  /**
   * Handle submit
   */
  const { lengthCentsCurrency } = useAppInfo()

  const handleSubmit = useCallback(
    async (formData: CreateSponsoredBrandsCampaignFormData) => {
      const preData = {
        ...formData,
        products: selectedProducts,
        audience_id: audienceId,
        publisher_id: rmid,
        lengthCentsCurrency
      }

      const body = DTOCreateSponsoredBrandsCampaign(preData)
      const action = handleDebugSubmit || mutate

      applyPreparedCampaignBody(body, prepareCampaignCreateBody, action)
    },
    [
      audienceId,
      lengthCentsCurrency,
      rmid,
      selectedProducts,
      handleDebugSubmit,
      mutate,
      prepareCampaignCreateBody
    ]
  )

  /**
   * Initial data
   */
  const initialData: Partial<CreateSponsoredBrandsCampaignFormData> = {
    type: 'sponsored_brand',
    name: defaultCampaignName,
    start_date: moment().format('YYYY-MM-DD'),
    budget: { daily: null },
    placements: { placementsCheck: 'all' },
    settings: withDefaultAgeRestrictedSetting({
      budget_type: 'fast_as_possible'
    }),
    subpublishers: {
      subpublishersCheck: 'all'
    }
  }

  return (
    <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
      <CampaignType />

      <NameAndTimeInterval
        formRef={formRef}
        labelTitle={t('rm:campaign.create.NameAndTimeInterval.labelTitle')}
        placeholderTitle={t(
          'rm:campaign.create.NameAndTimeInterval.placeholderTitle'
        )}
        hasDescription
        labelDescription={t(
          'rm:campaign.create.NameAndTimeInterval.labelDescription'
        )}
        placeholderDescription={t(
          'rm:campaign.create.NameAndTimeInterval.placeholderDescription'
        )}
        onlyDates
        endDateIsRequired={false}
        showValidBadgeOnSection
        intervalAlertMessage={[
          t('rm:campaign.create.NameAndTimeInterval.intervalAlertMessage')
        ]}
        showEndDateOptional
        titlePanel={t('rm:campaign.create.NameAndTimeInterval.titlePanel')}
        onChangeDate={setCampaignPeriod}
      />

      <Subpublishers />

      <CampaignTargeting
        handleSectionValidity={setTargetingContentIsValid}
        onChange={setCampaignTargeting}
        formRef={formRef}
      />

      {targetingContentIsValid && (
        <>
          <SponsoredBrand
            formRef={formRef}
            initialData={{ publisher_id: rmid }}
            handleIsValid={handleAssetsIsValid}
          />

          {/* No isRequired here on purpose: that flag exists only for the
              offsite briefing flow, where the campaign-wide URL becomes
              mandatory once some accepted media has none of its own. Onsite
              has no such fallback rule, so this panel is never forced. */}
          <CampaignDestinationUrl />

          <ProductsPanel
            formRef={formRef}
            description={t('rm:campaign.create.ProductsPanel.description')}
            publisherId={rmid}
            isRequired
          />

          {!!formIsValid.messages?.length && (
            <S.WrapperAlerts>
              {formIsValid.messages.map((message, index) => (
                <Alert
                  variant="warning"
                  key={index}
                  onClick={message?.action}
                  role={message?.action ? 'button' : 'text'}
                >
                  {message?.text || message}
                </Alert>
              ))}
            </S.WrapperAlerts>
          )}

          <Budget
            adType="sponsored_brand"
            formRef={formRef}
            publisherId={rmid}
            formData={formDataFinal}
            formIsValid={formIsValid}
            handleIsValid={setBudgetValueIsValid}
            initialValueDailyBudget={'R$ 100.00'}
          />

          <hr />

          <AdvancedSettings targeting={campaignTargeting.targeting} />

          <AlertErrorCampaign />

          {!budgetValueIsValid && (
            <Alert variant="critical" className="mb-2">
              {t('rm:campaign.form.alerts.missingInvestmentFields')}
            </Alert>
          )}

          {!assetsIsValid.logo && !allowBypassLogoValidation && (
            <Alert variant="critical" className="mb-2">
              {t('rm:campaign.form.alerts.missingLogo')}
            </Alert>
          )}

          {!assetsIsValid.assets && (
            <Alert variant="critical" className="mb-2">
              {t('rm:campaign.form.alerts.missingMedia')}
            </Alert>
          )}

          {!!handleDebugSubmit && (
            <Alert variant="critical">
              🚀 ~ DEBUG MODE ATIVADO: este modo imprime o payload da requisição
              no console e desabilita o envio do formulário.
            </Alert>
          )}

          <div className="form-default__buttons">
            {!!handleDebugSubmit && (
              <Button
                type="button"
                text={'Desativar modo debug'}
                onClick={toggleDebugMode}
                size="large"
                template="critical"
              />
            )}

            <Button
              type="submit"
              text={t('rm:sendForApproval')}
              loading={isPending}
              size="large"
              template="primary"
              disabled={!formIsReadyToSubmit}
            />
          </div>
        </>
      )}
    </Form>
  )
}

export default SponsoredBrands
